import { JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Flex, Image, useMediaQuery } from '@chakra-ui/react';

import rwdHintImage from '@/assets/image/rwd-hint.png';

import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import useAuth from '@/store/auth';
import useAdminAuth from '@/store/adminAuth';

import AdminLogin from '@/views/Admin/Login/Login';
import AdminMain from '@/views/Admin/Main/Main';
import AnalysisIndex from '@/views/Admin/Analysis/AnalysisIndex';
import AnalysisProvider from '@/views/Admin/AnalysisProvider/AnalysisProvider';
import Mailer from '@/views/Admin/Mailer/mailer';
import UserIndex from '@/views/Admin/User/UserIndex';

import CandidateDetails from '@/views/Job/Candidate/CandidateDetails';
import CandidateIndex from '@/views/Candidate/CandidateIndex';
import CandidateProfile from '@/views/Candidate/CandidateProfile';

import CreateJob from '@/views/Job/CreateJob/CreateJob';
import Home from '@/views/Home/Home';
import JobDetails from '@/views/Job/Details/JobDetails';
import JobIndex from '@/views/Job/JobIndex';

import ForgetPassword from '@/views/Login/ForgetPassword';
import Login from '@/views/Login/Login';
import ResetPassword from '@/views/Login/ResetPassword';

import Main from '@/views/Main/Main';
import Profile from '@/views/Profile/Profile';

import SignUpIndex from '@/views/SignUp/SignUpIndex';
import InvitationSignUp from '@/views/SignUp/InvitationSignUp';

import TrialIndex from '@/views/Trial/TrialIndex';

import InterviewAgentIndex from '@/views/InterviewAgent/InterviewAgentIndex';
import InterviewDetails from '@/views/Interview/Copilot/Details/InterviewDetails';
import InterviewIndex from '@/views/Interview/InterviewIndex';

import QuestionBankIndex from '@/views/Interview/Agent/QuestionBank/QuestionBankIndex';
import CreateUpdateAgentIndex from '@/views/Interview/Agent/CreateUpdateAgent/CreateUpdateAgentIndex';
import UpdateScreeningPage from '@/views/Interview/Agent/CreateUpdateAgent/UpdateScreeningPage';
import CreateScreeningWithCandidatePage from '@/views/Interview/Agent/CreateUpdateAgent/CreateScreeningWithCandidatePage';
import JobBoardIndex from '@/views/JobBoard/JobBoardIndex';
import JobBoardJobDetails from '@/views/JobBoard/JobDetails';
import InboxIndex from '@/views/Inbox/InboxIndex';
import CandidateApplicationQuestionsIndex from '@/views/CandidateApplicationQuestions/CandidateApplicationQuestionsIndex';
import VeteransResumeTranslatorIndex from '@/views/VeteransResumeTranslator/VeteransResumeTranslatorIndex';

const RequiredAuth = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  return children;
};

const RequiredAdminAuth = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn } = useAdminAuth();

  if (!isLoggedIn) {
    return <Navigate to="/admin/login" />;
  }

  return children;
};

const RwdHintOverlay = ({ children }: { children: JSX.Element }) => {
  const [isLessThan1280] = useMediaQuery(['(max-width: 1080px)'], {
    ssr: false
  });

  return (
    <>
      {isLessThan1280 && (
        <Flex
          pos="fixed"
          zIndex={500}
          top="0"
          left="0"
          w="100%"
          h="100%"
          bg="#f8f9fb"
          justifyContent="center"
          alignItems="center"
          px="12px"
        >
          <Image src={rwdHintImage} w="340px" h="auto" />
        </Flex>
      )}
      {children}
    </>
  );
};

const DocumentTitleManager = () => {
  useDocumentTitle();
  return null;
};

export default function Router() {
  return (
    <BrowserRouter>
      <DocumentTitleManager />
      <Routes>
        <Route
          path="login"
          element={
            <RwdHintOverlay>
              <Login />
            </RwdHintOverlay>
          }
        />
        <Route path="forget-password" element={<ForgetPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="signup" element={<SignUpIndex />} />
        <Route path="signup/invitation" element={<InvitationSignUp />} />
        <Route
          path="/"
          element={
            <RequiredAuth>
              <RwdHintOverlay>
                <Main />
              </RwdHintOverlay>
            </RequiredAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="job" element={<JobIndex />} />
          <Route path="job/create" element={<CreateJob />} />
          <Route path="job/details/:jobId" element={<JobDetails />} />
          <Route path="job/details/:jobId/candidate/:candidateId" element={<CandidateDetails />} />

          <Route path="candidate" element={<CandidateIndex />} />
          <Route path="candidate/:candidateId" element={<CandidateProfile />} />

          <Route path="interview" element={<InterviewIndex />} />
          <Route path="interview/:meetingId" element={<InterviewDetails />} />
          <Route path="interview/question-bank" element={<QuestionBankIndex />} />
          <Route path="interview/agent/create-session" element={<CreateUpdateAgentIndex />} />
          <Route path="interview/agent/update/:screeningId" element={<UpdateScreeningPage />} />
          <Route
            path="interview/agent/create/:candidateId"
            element={<CreateScreeningWithCandidatePage />}
          />

          <Route path="inbox" element={<InboxIndex />} />

          <Route path="profile" element={<Profile />} />
        </Route>

        <Route path="admin/login" element={<AdminLogin />} />
        <Route
          path="admin"
          element={
            <RequiredAdminAuth>
              <AdminMain />
            </RequiredAdminAuth>
          }
        >
          <Route path="analysis" element={<AnalysisIndex />} />
          <Route path="provider" element={<AnalysisProvider />} />
          <Route path="mailer" element={<Mailer />} />
          <Route path="users" element={<UserIndex />} />
        </Route>

        <Route path="resume_jd_analysis_trial" element={<TrialIndex />} />
        <Route path="screening" element={<InterviewAgentIndex />} />

        <Route path="job_board/:agencyId" element={<JobBoardIndex />} />
        <Route path="job_board/:agencyId/job/:jobId" element={<JobBoardJobDetails />} />
        <Route path="veterans_resume/:agencyId" element={<VeteransResumeTranslatorIndex />} />

        <Route path="application_questions" element={<CandidateApplicationQuestionsIndex />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
